import { useState } from 'react'
import { Alert } from '../../components/atoms/Alert'
import { Badge } from '../../components/atoms/Badge'
import { Button } from '../../components/atoms/Button'
import { Spinner } from '../../components/atoms/Spinner'
import { Paginator } from '../../components/molecules/Paginator'
import { TableShell } from '../../components/molecules/TableShell'
import { Th, Td } from '../../components/molecules/Table'
import { ReturnDecisionModal } from './ReturnDecisionModal'
import { ORDERSTATUS, ORDERSTATUSLABEL } from '../../../constants/order-status.constants'
import {
  useOrderManagementReturns,
  type OrderReturnListDto
} from '../../../viewmodels/useOrderManagementReturns'
import { useOrganisations, type OrganisationDto } from '../../../viewmodels/useOrganisations'

const RETURN_LIFECYCLE_NEXT: Record<string, string | null> = {
  pending: null,
  accepted: ORDERSTATUS.RETURNPICKUP,
  return_confirmed: ORDERSTATUS.RETURNPICKUP,
  return_pickup: ORDERSTATUS.RETURNRECEIVED,
  return_received: ORDERSTATUS.REFUNDPROCESSED,
  refund_processed: ORDERSTATUS.REFUNDED,
  refunded: null,
  rejected: null
}

/**
 * /order-management/returns — operator view of every return request.
 *
 * Flat table — one row per return with reason, status, requested date,
 * and the customer's name / email / phone. Read-only in v1.
 */
export function OrderManagementReturnsPage(): JSX.Element {
  // '' = "All organisations" (no filter); otherwise the org id.
  const [orgFilter, setOrgFilter] = useState<string>('')
  const vm = useOrderManagementReturns(orgFilter === '' ? null : orgFilter)
  const orgsVm = useOrganisations()
  const [decisionTarget, setDecisionTarget] = useState<
    { row: OrderReturnListDto; decision: 'accept' | 'reject' } | null
  >(null)

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-h2 text-charcoal">Returns</h1>
          <p className="font-body text-body-sm text-text-secondary mt-1">
            Every customer return request. Reason + status + customer contact in one view.
          </p>
        </div>
        <label className="block">
          <span className="font-body text-body-xs uppercase tracking-wide text-text-secondary">
            Organisation
          </span>
          <select
            value={orgFilter}
            onChange={(e) => setOrgFilter(e.target.value)}
            disabled={orgsVm.isLoading}
            className="mt-1 w-64 rounded-md border border-border bg-bg-primary p-2 font-body text-body-sm text-charcoal disabled:bg-bg-secondary disabled:text-text-secondary"
          >
            <option value="">All organisations</option>
            {orgsVm.items.map((o: OrganisationDto) => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
          </select>
        </label>
      </header>

      {vm.error !== null ? (
        <Alert tone="error">
          {vm.error}{' '}
          <button
            type="button"
            className="underline underline-offset-2 hover:text-charcoal"
            onClick={vm.reload}
          >
            Retry
          </button>
        </Alert>
      ) : null}

      {vm.isLoading && vm.returns.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <Spinner />
        </div>
      ) : vm.returns.length === 0 ? (
        <Placeholder>
          {orgFilter === ''
            ? 'No returns yet.'
            : 'No returns for this organisation yet.'}
        </Placeholder>
      ) : (
        <>
          <TableShell>
            <table className="w-full font-body text-body-sm">
              <thead className="bg-parchment/50 text-text-secondary">
                <tr>
                  <Th>Requested</Th>
                  <Th>Customer</Th>
                  <Th>Reason</Th>
                  <Th>Status</Th>
                  <Th>Actions</Th>
                </tr>
              </thead>
              <tbody>
                {vm.returns.map((r) => (
                  <ReturnRow
                    key={r.id}
                    row={r}
                    onTransition={vm.transition}
                    onOpenDecision={(decision) => setDecisionTarget({ row: r, decision })}
                  />
                ))}
              </tbody>
            </table>
          </TableShell>
          <Paginator
            page={vm.page}
            pageSize={vm.pageSize}
            total={vm.total}
            onPageChange={vm.setPage}
            disabled={vm.isLoading}
          />
        </>
      )}
      {decisionTarget !== null ? (
        <ReturnDecisionModal
          open={decisionTarget !== null}
          row={decisionTarget.row}
          decision={decisionTarget.decision}
          onClose={() => setDecisionTarget(null)}
          onConfirm={async (nextStatus, notes) =>
            vm.transition(decisionTarget.row.id, nextStatus, notes)
          }
        />
      ) : null}
    </div>
  )
}

function customerName(row: OrderReturnListDto): string {
  const name = [row.customer_first_name, row.customer_last_name].filter(Boolean).join(' ')
  return name.length > 0 ? name : '—'
}

function statusTone(status: string): 'success' | 'warning' | 'error' | 'info' {
  switch (status) {
    case 'accepted':
    case 'processed':
      return 'success'
    case 'pending':
      return 'warning'
    case 'rejected':
      return 'error'
    default:
      return 'info'
  }
}

function formatDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  const y = d.getFullYear()
  const mo = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${mo}-${day}`
}

function ReturnRow({
  row,
  onTransition,
  onOpenDecision
}: {
  readonly row: OrderReturnListDto
  readonly onTransition: (returnId: string, nextStatus: string) => Promise<{ ok: true } | { ok: false; message: string }>
  readonly onOpenDecision: (decision: 'accept' | 'reject') => void
}): JSX.Element {
  const next = RETURN_LIFECYCLE_NEXT[row.status] ?? null
  return (
    <tr className="border-t border-border align-top">
      <Td className="whitespace-nowrap text-text-secondary">{formatDate(row.requested_at)}</Td>
      <Td>
        <div>{customerName(row)}</div>
        <div className="font-mono text-body-xs text-text-secondary">{row.customer_email}</div>
      </Td>
      <Td className="max-w-[40ch] whitespace-pre-wrap">{row.reason}</Td>
      <Td>
        <Badge tone={statusTone(row.status)}>{row.status}</Badge>
      </Td>
      <Td>
        <div className="flex flex-wrap gap-2">
          {row.status === 'pending' ? (
            <>
              <Button size="sm" variant="primary" onClick={() => onOpenDecision('accept')}>
                Accept
              </Button>
              <Button size="sm" variant="secondary" onClick={() => onOpenDecision('reject')}>
                Reject
              </Button>
            </>
          ) : next !== null ? (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => void onTransition(row.id, next)}
            >
              Mark {ORDERSTATUSLABEL[next as keyof typeof ORDERSTATUSLABEL]}
            </Button>
          ) : null}
        </div>
      </Td>
    </tr>
  )
}

function Placeholder({ children }: { readonly children: React.ReactNode }): JSX.Element {
  return (
    <div className="text-center py-16 border border-border rounded-md bg-parchment/40">
      <p className="font-body text-body-sm text-text-secondary">{children}</p>
    </div>
  )
}
